/**
 * <drag-cursor> — Custom Element that replaces the native cursor with a
 * follow-mouse "DRAG" indicator while the user hovers a draggable region
 * (typically a <swiper-init>). Desktop only — disabled below the
 * `lap_and_up` breakpoint (≤1024px) and on coarse pointer devices.
 *
 * Usage in liquid (wrap the swiper):
 *   <drag-cursor data-label="DRAG">
 *     <swiper-init ...>...</swiper-init>
 *   </drag-cursor>
 *
 * The label text comes from the `general.slider.drag_label` translation key
 * (set in `locales/*.json`). Override per-instance with `data-label="..."`.
 *
 * State:
 *   data-active="true"  — pointer is inside the region (cursor visible)
 *   data-pressed="true" — mouse button is down (grabbing)
 *
 * Loaded as ES module via importmap alias `@theme/drag-cursor` and globally
 * from `layout/theme.liquid` so the element is defined wherever it is used.
 */

import { responsive } from '@theme/responsive';

class DragCursor extends HTMLElement {
  #cursor = null;
  #x = 0;
  #y = 0;
  #raf = 0;
  #enabled = false;

  connectedCallback() {
    if (!this.#supportsHover()) return;

    this.#buildCursor();
    this.#updateEnabled();

    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerdown', this.#onDown);
    this.addEventListener('pointerup', this.#onUp);
    this.addEventListener('pointercancel', this.#onUp);

    document.addEventListener('breakpoint:changed', this.#updateEnabled);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerdown', this.#onDown);
    this.removeEventListener('pointerup', this.#onUp);
    this.removeEventListener('pointercancel', this.#onUp);
    document.removeEventListener('breakpoint:changed', this.#updateEnabled);
    this.#cursor?.remove();
    this.#cursor = null;
  }

  #supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  #buildCursor() {
    const label = this.getAttribute('data-label') || '';
    const cursor = document.createElement('span');
    cursor.className = 'DragCursor__visual';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = `
      <span class="DragCursor__arrow DragCursor__arrow--prev" aria-hidden="true">
        <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.27367 9.59172L12.688 15.526L12.1948 15.9823L5.78041 10.0481L6.27367 9.59172Z" fill="currentColor"/>
          <path d="M5.87277 9.91653L12.2871 3.9823L12.7804 4.43865L6.36603 10.3729L5.87277 9.91653Z" fill="currentColor"/>
        </svg>
      </span>
      <span class="DragCursor__label u-h3">${label}</span>
      <span class="DragCursor__arrow DragCursor__arrow--next" aria-hidden="true">
        <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3984 10.3729L6.98402 4.43865L7.47729 3.9823L13.8917 9.91653L13.3984 10.3729Z" fill="currentColor"/>
          <path d="M13.7993 10.0481L7.38493 15.9823L6.89166 15.526L13.306 9.59172L13.7993 10.0481Z" fill="currentColor"/>
        </svg>
      </span>
    `;
    this.appendChild(cursor);
    this.#cursor = cursor;
  }

  #updateEnabled = () => {
    this.#enabled = responsive.matchesBreakpoint('lap_and_up');
    if (!this.#enabled) {
      this.removeAttribute('data-active');
      this.removeAttribute('data-pressed');
    }
  };

  #isOverProductCardActions(target) {
    return target instanceof Element && !!target.closest('.product-card__actions');
  }

  #setActiveFromPointer(target) {
    if (this.#isOverProductCardActions(target)) {
      this.removeAttribute('data-active');
      this.removeAttribute('data-pressed');
      return;
    }

    this.setAttribute('data-active', 'true');
  }

  #onEnter = (event) => {
    if (!this.#enabled || event.pointerType !== 'mouse') return;
    this.#setActiveFromPointer(event.target);
    this.#schedulePosition();
  };

  #onLeave = () => {
    this.removeAttribute('data-active');
    this.removeAttribute('data-pressed');
  };

  #onMove = (event) => {
    if (!this.#enabled || event.pointerType !== 'mouse') return;
    this.#setActiveFromPointer(event.target);
    const rect = this.getBoundingClientRect();
    this.#x = event.clientX - rect.left;
    this.#y = event.clientY - rect.top;
    this.#schedulePosition();
  };

  #onDown = (event) => {
    if (!this.#enabled || event.pointerType !== 'mouse') return;
    if (this.#isOverProductCardActions(event.target)) return;
    this.setAttribute('data-pressed', 'true');
  };

  #onUp = () => {
    this.removeAttribute('data-pressed');
  };

  #schedulePosition() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      if (this.#cursor) {
        this.#cursor.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) translate(-50%, -50%)`;
      }
    });
  }
}

if (!customElements.get('drag-cursor')) {
  customElements.define('drag-cursor', DragCursor);
}
